import type { CSSProperties, ReactNode } from 'react';
import { AlertTriangle, Inbox, Loader2, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDeferredLoading } from '@/hooks/useLoadingUx';
import { Button } from './Button';

/* -------------------------------------------------------------------------- */
/* Spinners                                                                   */
/* -------------------------------------------------------------------------- */

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn('size-5 animate-spin text-brand-600', className)} aria-hidden />;
}

/**
 * Three-dot pulse. Quieter than a spinner and cheaper to animate — used where a
 * loader sits next to text rather than replacing a region.
 */
export function DotPulse({ className, tone = 'brand' }: { className?: string; tone?: 'brand' | 'white' }) {
  return (
    <span className={cn('inline-flex items-center gap-1', className)} aria-hidden>
      {[0, 1, 2].map((index) => (
        <span
          key={index}
          className={cn(
            'size-1.5 rounded-full',
            tone === 'white' ? 'bg-white' : 'bg-brand-600',
          )}
          style={{ animation: 'dot-pulse 1.1s ease-in-out infinite', animationDelay: `${index * 0.14}s` }}
        />
      ))}
    </span>
  );
}

export function LoadingBlock({
  label = 'Loading…',
  className,
}: {
  label?: string;
  className?: string;
}) {
  return (
    <div
      className={cn('flex flex-col items-center justify-center gap-3 py-12', className)}
      role="status"
      aria-live="polite"
    >
      <Spinner />
      <p className="text-sm text-ink-muted">{label}</p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Skeletons                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * A shimmering placeholder shaped like the content it stands in for.
 *
 * Skeletons beat spinners wherever the final layout is known: the page does not
 * jump when the data lands, and the user reads the shape of the answer while
 * they wait.
 */
export function Skeleton({
  className,
  dark = false,
  style,
}: {
  className?: string;
  dark?: boolean;
  /** For heights computed at runtime, where a Tailwind class cannot be generated. */
  style?: CSSProperties;
}) {
  return <div className={cn(dark ? 'skeleton-dark' : 'skeleton', className)} style={style} aria-hidden />;
}

/** Table body placeholder that keeps the column count stable while loading. */
export function SkeletonRows({ rows = 5, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, rowIndex) => (
        <tr key={rowIndex} className="border-t border-line">
          {Array.from({ length: columns }, (_, columnIndex) => (
            <td key={columnIndex} className="px-4 py-3.5">
              <Skeleton
                className={cn('h-4', columnIndex === 0 ? 'w-24' : 'w-full max-w-[140px]')}
                // A uniform shimmer across a whole table looks mechanical; a small
                // per-cell offset makes it read as one surface settling.
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

/** Placeholder for a form: label + control pairs at the real control height. */
export function SkeletonForm({ fields = 4, className }: { fields?: number; className?: string }) {
  return (
    <div className={cn('grid gap-4 md:grid-cols-2', className)} aria-hidden>
      {Array.from({ length: fields }, (_, index) => (
        <div key={index} className="flex flex-col gap-2">
          <Skeleton className="h-3.5 w-28" />
          <Skeleton className="h-[42px] w-full rounded-xl" />
        </div>
      ))}
    </div>
  );
}

/** Placeholder for a list of avatar/title/subtitle rows. */
export function SkeletonList({ rows = 5, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn('flex flex-col', className)} aria-hidden>
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="flex items-center gap-3 border-b border-line py-3 last:border-0">
          <Skeleton className="size-9 shrink-0 rounded-xl" />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Skeleton className="h-3.5 w-1/3" />
            <Skeleton className="h-3 w-2/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Overlays                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Full-screen wait, for the two moments where there is genuinely nothing to
 * show yet: signing in, and the first paint of a code-split route.
 *
 * Held behind {@link useDeferredLoading} so a warm navigation never flashes it.
 */
export function FullPageLoader({
  label = 'Loading…',
  sublabel,
  transparent = false,
}: {
  label?: string;
  sublabel?: string;
  transparent?: boolean;
}) {
  return (
    <div
      className={cn(
        'animate-reveal fixed inset-0 z-50 flex flex-col items-center justify-center gap-4',
        transparent ? 'bg-canvas/70 backdrop-blur-sm' : 'bg-canvas',
      )}
      role="status"
      aria-live="polite"
    >
      <span className="relative flex size-14 items-center justify-center">
        <span className="absolute inset-0 rounded-2xl bg-brand-100" />
        <span className="absolute inset-0 animate-ping rounded-2xl bg-brand-200/60" />
        <Loader2 className="relative size-7 animate-spin text-brand-600" aria-hidden />
      </span>
      <div className="text-center">
        <p className="text-sm font-bold text-ink">{label}</p>
        {sublabel && <p className="mt-0.5 text-[13px] text-ink-muted">{sublabel}</p>}
      </div>
    </div>
  );
}

/**
 * Covers a card or section while it refetches, without collapsing its height —
 * the content stays readable underneath, which is far less jarring than swapping
 * a populated card for a spinner.
 */
export function OverlayLoader({ show, label }: { show: boolean; label?: string }) {
  const visible = useDeferredLoading(show, { delay: 220, minDuration: 400 });
  if (!visible) return null;

  return (
    <div
      className="absolute inset-0 z-10 flex items-center justify-center rounded-[inherit] bg-white/65 backdrop-blur-[1px]"
      role="status"
      aria-live="polite"
    >
      <span className="flex items-center gap-2.5 rounded-full border border-line bg-white px-3.5 py-2 shadow-card">
        <Spinner className="size-4" />
        <span className="text-xs font-semibold text-ink-soft">{label ?? 'Updating…'}</span>
      </span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Empty / error                                                              */
/* -------------------------------------------------------------------------- */

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-center justify-center px-6 py-14 text-center', className)}>
      <span className="mb-4 inline-flex size-12 items-center justify-center rounded-2xl bg-canvas text-ink-muted [&>svg]:size-6">
        {icon ?? <Inbox aria-hidden />}
      </span>
      <h3 className="text-sm font-bold text-ink">{title}</h3>
      {description && <p className="mt-1 max-w-sm text-sm text-ink-muted">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function ErrorState({
  title = 'Something went wrong',
  message,
  onRetry,
  className,
}: {
  title?: string;
  message?: string;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-center justify-center px-6 py-12 text-center', className)}>
      <span className="mb-4 inline-flex size-12 items-center justify-center rounded-2xl bg-red-50 text-red-600">
        <AlertTriangle className="size-6" aria-hidden />
      </span>
      <h3 className="text-sm font-bold text-ink">{title}</h3>
      <p className="mt-1 max-w-sm text-sm text-ink-muted">
        {message ?? 'The request could not be completed. Please try again.'}
      </p>
      {onRetry && (
        <Button variant="secondary" size="sm" className="mt-5" onClick={onRetry} leftIcon={<RefreshCw className="size-4" />}>
          Try again
        </Button>
      )}
    </div>
  );
}

/** Inline callout used for scan results and form-level messages. */
export function Alert({
  tone = 'info',
  title,
  children,
  icon,
  className,
}: {
  tone?: 'info' | 'success' | 'warning' | 'error';
  title?: ReactNode;
  children?: ReactNode;
  icon?: ReactNode;
  className?: string;
}) {
  const tones = {
    info: 'bg-brand-50 text-brand-800 border-brand-100',
    success: 'bg-receive-50 text-receive-700 border-receive-100',
    warning: 'bg-stock-50 text-stock-600 border-stock-100',
    error: 'bg-red-50 text-red-700 border-red-100',
  };
  return (
    <div className={cn('flex items-start gap-3 rounded-xl border px-4 py-3', tones[tone], className)} role="alert">
      {icon && <span className="mt-0.5 shrink-0 [&>svg]:size-4.5">{icon}</span>}
      <div className="min-w-0 text-sm">
        {title && <p className="font-bold">{title}</p>}
        {children && <div className={cn(title && 'mt-0.5', 'text-[13px] leading-relaxed opacity-90')}>{children}</div>}
      </div>
    </div>
  );
}
